import React from "react";
import { Link } from "react-router-dom";
import { Menu, Search, Sun, Moon, LogOut, CreditCard, ChevronDown } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { LOGOS } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import NotificationsBell from "@/components/NotificationsBell";
import AthleteSelector from "@/components/layout/AthleteSelector";
import QuickVoiceNote from "@/components/voice/QuickVoiceNote";
import { useLang, translateFr as tfr } from "@/i18n";

export default function Topbar({ onMenu, onSearch }) {
  const { t } = useLang();
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const logoSrc = theme === "dark" ? LOGOS.white : LOGOS.black;

  return (
    <header
      className="sticky top-0 z-40 h-16 border-b border-border bg-surface/80 backdrop-blur-sm pt-[env(safe-area-inset-top)]"
      data-testid="topbar"
    >
      <div className="flex h-16 items-center justify-between gap-2 px-4 md:px-6">
        {/* Left: hamburger + logo (mobile only) */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={onMenu}
            className="h-10 w-10 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label={tfr("Ouvrir le menu")}
            data-testid="topbar-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/dashboard" data-testid="topbar-logo-link">
            <img src={logoSrc} alt="Athlynx" className="h-7 w-auto" />
          </Link>
        </div>

        <div className="hidden lg:block min-w-0 max-w-[280px] flex-1" data-testid="topbar-athlete-selector">
          <AthleteSelector />
        </div>

        {/* Right */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={onSearch}
            className="hidden md:flex items-center gap-2 h-9 px-3 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors text-xs"
            data-testid="topbar-search-btn"
            aria-label="Recherche globale"
          >
            <Search className="h-3.5 w-3.5" />
            <span>{t("nav.search")}</span>
            <kbd className="ml-2 inline-flex items-center rounded border border-border px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground/70">⌘K</kbd>
          </button>
          <button
            onClick={onSearch}
            className="md:hidden h-10 w-10 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label="Recherche globale"
            data-testid="topbar-search-btn-mobile"
          >
            <Search className="h-4 w-4" />
          </button>

          <NotificationsBell />

          <QuickVoiceNote />

          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-btn" aria-label={tfr("Changer de thème")}>
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 rounded-full lg:rounded-lg p-1 lg:pl-1.5 lg:pr-2 hover:bg-secondary/60 transition-colors"
                data-testid="profile-menu-btn"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-secondary text-xs">
                    {user?.name?.[0]?.toUpperCase() || "A"}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden lg:block text-left">
                  <div className="text-xs font-semibold leading-tight truncate max-w-[130px]">{user?.name || "Agent"}</div>
                  <div className="text-[10px] text-muted-foreground leading-tight">Agent</div>
                </div>
                <ChevronDown className="hidden lg:block h-3.5 w-3.5 text-muted-foreground/60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{user?.name}</span>
                  <span className="text-xs text-muted-foreground">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild data-testid="settings-menu-item">
                <Link to="/settings">{t("nav.settings")}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="billing-menu-item">
                <Link to="/billing" className="flex items-center gap-2"><CreditCard className="h-4 w-4" /> {tfr("Facturation")}</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-danger focus:text-danger" data-testid="logout-menu-item">
                <LogOut className="h-4 w-4 mr-2" /> {t("nav.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
